import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {ArrowLeft } from "lucide-react";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCourses = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add course
  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    price: "",
    instructor: "",
    banner: "",
  });

  // Add chapter
  const [showChapterModal, setShowChapterModal] = useState(false);
  const [chapterForm, setChapterForm] = useState({
    course_id: "",
    title: "",
    video_url: "",
    duration: "",
  });

  // View chapters
  const [showChaptersModal, setShowChaptersModal] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  // Edit chapter
  const [showEditChapterModal, setShowEditChapterModal] = useState(false);
  const [editChapterForm, setEditChapterForm] = useState({
    id: "",
    title: "",
    video_url: "",
    duration: "",
  });

  // ================= FETCH COURSES =================
  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`);
      setCourses(res.data);
    } catch {
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // ================= ADD COURSE =================
  const handleAddCourse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/courses`, courseForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Course added");
      setCourseForm({
        title: "",
        description: "",
        price: "",
        instructor: "",
        banner: "",
      });
      fetchCourses();
    } catch {
      toast.error("Failed to add course");
    }
  };

  // ================= DELETE COURSE =================
  const handleDeleteCourse = async (id) => {
    if (!window.confirm("Delete this course?")) return;

    try {
      await axios.delete(`${API}/admin/courses/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Course deleted");
      fetchCourses();
    } catch {
      toast.error("Failed to delete course");
    }
  };

  // ================= ADD CHAPTER =================
  const handleAddChapter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/chapters`,
        { ...chapterForm, order: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Chapter added");
      setShowChapterModal(false);
      setChapterForm({
        course_id: "",
        title: "",
        video_url: "",
        duration: "",
      });
      fetchCourses();
    } catch {
      toast.error("Failed to add chapter");
    }
  };

  // ================= FETCH CHAPTERS =================
  const fetchChapters = async (courseId) => {
    try {
      const res = await axios.get(
        `${API}/admin/courses/${courseId}/chapters`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChapters(res.data);
      setSelectedCourseId(courseId);
      setShowChaptersModal(true);
    } catch {
      toast.error("Failed to load chapters");
    }
  };

  // ================= DELETE CHAPTER =================
  const handleDeleteChapter = async (chapterId) => {
    if (!window.confirm("Delete this chapter?")) return;

    try {
      await axios.delete(`${API}/admin/chapters/${chapterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Chapter deleted");
      fetchChapters(selectedCourseId);
      fetchCourses();
    } catch {
      toast.error("Failed to delete chapter");
    }
  };

  // ================= UPDATE CHAPTER =================
  const handleUpdateChapter = async (e) => {
    e.preventDefault();

    try {
      await axios.put(
        `${API}/admin/chapters/${editChapterForm.id}`,
        {
          course_id: selectedCourseId,
          title: editChapterForm.title,
          video_url: editChapterForm.video_url,
          duration: editChapterForm.duration,
          order: 1,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Chapter updated");
      setShowEditChapterModal(false);
      fetchChapters(selectedCourseId);
      fetchCourses();
    } catch {
      toast.error("Failed to update chapter");
    }
  };

  if (loading) return <p className="p-6">Loading...</p>;

  return (
    
    <div className="p-6 space-y-10">
      <Button variant="ghost" onClick={() => navigate("/admin")} className="mb-6 border">
        <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>

      <h1 className="text-3xl font-bold text-[#0f3e3e]">
        Manage Courses
      </h1>

      {/* ADD COURSE */}
      <form
        onSubmit={handleAddCourse}
        className="bg-white border p-6 rounded-lg space-y-4"
      >
        <h2 className="text-xl font-semibold">Add New Course</h2>

        <Input placeholder="Course Title" value={courseForm.title}
          onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} required />

        <Textarea placeholder="Description" value={courseForm.description}
          onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} required />

        <Input type="number" placeholder="Price" value={courseForm.price}
          onChange={(e) => setCourseForm({ ...courseForm, price: e.target.value })} required />

        <Input placeholder="Instructor Name" value={courseForm.instructor}
          onChange={(e) => setCourseForm({ ...courseForm, instructor: e.target.value })} required />

        <Input placeholder="Banner Image URL" value={courseForm.banner}
          onChange={(e) => setCourseForm({ ...courseForm, banner: e.target.value })} required />

        <Button className="bg-[#0f3e3e] text-white">Add Course</Button>
      </form>

      {/* COURSE LIST */}
      <div className="grid md:grid-cols-2 gap-6">
        {courses.map((course) => (
          <div key={course.id} className="border p-6 rounded-lg bg-white space-y-3">
            <h3 className="text-xl font-semibold">{course.title}</h3>
            <p className="text-sm text-muted-foreground">
              Chapters: {course.chapter_count}
            </p>

            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => {
                setShowChapterModal(true);
                setChapterForm({ ...chapterForm, course_id: course.id });
              }}>
                + Add Chapter
              </Button>

              <Button variant="secondary" onClick={() => fetchChapters(course.id)}>
                View Chapters
              </Button>

              <Button variant="destructive" onClick={() => handleDeleteCourse(course.id)}>
                Delete Course
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* ADD CHAPTER MODAL */}
      {showChapterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Add Chapter</h2>

            <form onSubmit={handleAddChapter} className="space-y-4">
              <Input placeholder="Chapter Title" value={chapterForm.title}
                onChange={(e) => setChapterForm({ ...chapterForm, title: e.target.value })} required />

              <Input placeholder="YouTube Video URL" value={chapterForm.video_url}
                onChange={(e) => setChapterForm({ ...chapterForm, video_url: e.target.value })} required />

              <Input placeholder="Duration (eg: 12:30)" value={chapterForm.duration}
                onChange={(e) => setChapterForm({ ...chapterForm, duration: e.target.value })} required />

              <div className="flex justify-end gap-2">
                <Button variant="outline" type="button" onClick={() => setShowChapterModal(false)}>
                  Cancel
                </Button>
                <Button className="bg-[#0f3e3e] text-white">Add Chapter</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW / EDIT / DELETE CHAPTERS */}
      {showChaptersModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-lg">
            <h2 className="text-xl font-semibold mb-4">Course Chapters</h2>

            {chapters.map((ch) => (
              <div key={ch.id} className="border p-3 rounded mb-3 flex justify-between">
                <div>
                  <p className="font-medium">{ch.title}</p>
                  <p className="text-xs text-muted-foreground">
                    Duration: {ch.duration}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditChapterForm(ch);
                    setShowEditChapterModal(true);
                  }}>
                    Edit
                  </Button>
                  <Button size="sm" variant="destructive"
                    onClick={() => handleDeleteChapter(ch.id)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}

            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setShowChaptersModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT CHAPTER MODAL */}
      {showEditChapterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Edit Chapter</h2>

            <form onSubmit={handleUpdateChapter} className="space-y-4">
              <Input value={editChapterForm.title}
                onChange={(e) => setEditChapterForm({ ...editChapterForm, title: e.target.value })} />

              <Input value={editChapterForm.video_url}
                onChange={(e) => setEditChapterForm({ ...editChapterForm, video_url: e.target.value })} />

              <Input value={editChapterForm.duration}
                onChange={(e) => setEditChapterForm({ ...editChapterForm, duration: e.target.value })} />

              <div className="flex justify-end gap-2">
                <Button variant="outline" type="button"
                  onClick={() => setShowEditChapterModal(false)}>
                  Cancel
                </Button>
                <Button className="bg-[#0f3e3e] text-white">
                  Update Chapter
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCourses;
